/**
 * Mode-Aware ERP Client
 *
 * Provides unified interface for ERP operations that works in both:
 * - direct mode: Uses session-based FrappeClient
 * - control_plane mode: Uses JWT-based tenantServer
 */

import { getFrappeClient as getFrappeClientFromCore } from "@/lib/frappe-client";
import { tenantServer } from "@/lib/api/tenantServerClient";
import { useAuthStore } from "@/lib/store/authStore";
import type { FrappeClient, FrappeListParams } from "@/lib/frappe-client";

/**
 * Unified ERP client interface
 */
export interface ERPClient {
  list<T>(params: FrappeListParams): Promise<T[]>;
  listWithPagination<T>(params: FrappeListParams): Promise<{ data: T[]; total: number; pageLength: number }>;
  get<T>(params: { doctype: string; name: string }): Promise<T>;
  create<T>(params: { doctype: string; data: Record<string, any> }): Promise<T>;
  update<T>(params: { doctype: string; name: string; data: Record<string, any> }): Promise<T>;
  delete(params: { doctype: string; name: string }): Promise<void>;
  call<T>(method: string, args?: Record<string, any>): Promise<T>;
  callBlob(method: string, args?: Record<string, any>): Promise<Blob>;
  callWithFormData<T>(method: string, formData: FormData): Promise<T>;
  getCount(doctype: string, filters?: Record<string, any>): Promise<number>;
  rename(params: { doctype: string; oldName: string; newName: string }): Promise<string>;
}

/**
 * Get the appropriate ERP client based on auth mode.
 *
 * In control_plane mode: Returns JWT-based tenantServer wrapper
 * In direct mode: Returns session-based FrappeClient wrapper
 */
export function getERPClient(): ERPClient {
  const authMode = useAuthStore.getState().authMode;

  if (authMode === 'control_plane') {
    return {
      list: (params) => tenantServer.list(params.doctype, {
        filters: params.filters,
        fields: params.fields,
        limit_start: params.limit_start,
        limit_page_length: params.limit_page_length,
        order_by: params.order_by,
      }),
      listWithPagination: (params) => tenantServer.listWithPagination(params.doctype, {
        filters: params.filters,
        fields: params.fields,
        limit_start: params.limit_start,
        limit_page_length: params.limit_page_length,
        order_by: params.order_by,
      }),
      get: (params) => tenantServer.get(params.doctype, params.name),
      create: (params) => tenantServer.create(params.doctype, params.data),
      update: (params) => tenantServer.update(params.doctype, params.name, params.data),
      delete: (params) => tenantServer.delete(params.doctype, params.name),
      call: (method, args) => tenantServer.call(method, args),
      callBlob: (method, args) => tenantServer.callBlob(method, args),
      callWithFormData: (method, formData) => tenantServer.callWithFormData(method, formData),
      getCount: (doctype, filters) => tenantServer.getCount(doctype, filters),
      rename: (params) => tenantServer.call('zaviago_backend.api.rename_doc.rename_with_suffix', {
        doctype: params.doctype, old_name: params.oldName, new_name: params.newName,
      }),
    };
  }

  // Direct mode - use existing FrappeClient
  const client = getFrappeClientFromCore();
  return {
    list: (params) => client.list(params),
    listWithPagination: (params) => client.listWithPagination(params),
    get: (params) => client.get(params),
    create: (params) => client.create(params),
    update: (params) => client.update(params),
    delete: (params) => client.delete(params),
    call: (method, args) => client.call(method, args),
    callBlob: async (method, args) => {
      // Access internal getAuthHeaders for CSRF token (same pattern as files.ts)
      const getAuthHeaders = (client as any).getAuthHeaders as (() => Promise<HeadersInit>) | undefined;
      const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch(`/api/method/${method}`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: args ? JSON.stringify(args) : undefined,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.message || error?.exc_type || 'Request failed');
      }
      return response.blob();
    },
    callWithFormData: async (method, formData) => {
      const res = await fetch(`/api/method/${method}`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        let message = "Upload failed";
        try {
          if (err?._server_messages) {
            const msgs = JSON.parse(err._server_messages);
            const first = typeof msgs[0] === "string" ? JSON.parse(msgs[0]) : msgs[0];
            message = first?.message || message;
          } else if (err?.message) {
            message = err.message;
          }
        } catch {
          message = err?.exc_type || "Upload failed";
        }
        throw new Error(message);
      }
      const data = await res.json();
      return data.message;
    },
    getCount: (doctype, filters) => client.getCount(doctype, filters),
    rename: (params) => client.call('zaviago_backend.api.rename_doc.rename_with_suffix', {
      doctype: params.doctype, old_name: params.oldName, new_name: params.newName,
    }),
  };
}

/**
 * Legacy export for backwards compatibility.
 *
 * @deprecated Use getERPClient() instead
 * @throws Error in control_plane mode
 */
export function getFrappeClient(): FrappeClient {
  const authMode = useAuthStore.getState().authMode;
  if (authMode === 'control_plane') {
    throw new Error(
      'getFrappeClient() is not available in control_plane mode. ' +
      'Use getERPClient() instead.'
    );
  }
  return getFrappeClientFromCore();
}
